// Analytics page — Phase 6f.
//
// Route: /analytics. Five KPIs over the last 30 days:
//   1. Median first-reply time (hours)
//   2. Daily volume by brand (30-day bar chart)
//   3. Haiku acceptance % (drafts that got sent)
//   4. Backlog age (median hours on open conversations)
//   5. Peak hours (hour-of-day distribution of inbound messages)
//
// Charts are hand-rolled inline SVG — no heavy charting lib needed for
// two small visualizations, and it keeps the bundle slim. Kimi's
// mockup aesthetic carries through (monstera fills, eggshell axes).

import { useNavigate } from 'react-router-dom';
import { Logo } from '@/components/brand/Logo';
import { useAuth } from '@/auth/AuthProvider';
import { useAnalytics, type DailyVolumePoint, type HourBucket } from '@/lib/hooks/useAnalytics';

function formatHours(h: number | null): string {
  if (h === null) return '—';
  if (h < 1) return `${Math.round(h * 60)}m`;
  if (h < 48) return `${h.toFixed(1)}h`;
  return `${(h / 24).toFixed(1)}d`;
}

function formatPct(p: number | null): string {
  if (p === null) return '—';
  return `${Math.round(p)}%`;
}

function KpiCard({
  label,
  value,
  sub,
}: {
  label: string;
  value: string;
  sub: string;
}) {
  return (
    <div
      style={{
        background: 'white',
        border: '1px solid hsl(var(--ink) / 0.1)',
        borderRadius: 12,
        padding: '18px 20px',
      }}
    >
      <div
        style={{
          fontSize: 11,
          letterSpacing: '0.1em',
          textTransform: 'uppercase',
          color: 'hsl(var(--ink) / 0.55)',
          marginBottom: 8,
        }}
      >
        {label}
      </div>
      <div style={{ fontSize: 28, fontWeight: 700, color: 'hsl(var(--ink))', lineHeight: 1.1 }}>
        {value}
      </div>
      <div style={{ fontSize: 12, color: 'hsl(var(--ink) / 0.55)', marginTop: 4 }}>{sub}</div>
    </div>
  );
}

function DailyVolumeChart({ data }: { data: DailyVolumePoint[] }) {
  const width = 720;
  const height = 180;
  const pad = { top: 16, right: 12, bottom: 28, left: 28 };
  const innerW = width - pad.left - pad.right;
  const innerH = height - pad.top - pad.bottom;
  const max = Math.max(1, ...data.map((d) => d.md + d.hk));
  const barW = innerW / data.length;
  return (
    <div
      style={{
        background: 'white',
        border: '1px solid hsl(var(--ink) / 0.1)',
        borderRadius: 12,
        padding: 20,
      }}
    >
      <div
        style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          marginBottom: 12,
        }}
      >
        <div
          style={{
            fontSize: 11,
            letterSpacing: '0.1em',
            textTransform: 'uppercase',
            color: 'hsl(var(--ink) / 0.55)',
          }}
        >
          Daily inbound volume · last 30 days
        </div>
        <div style={{ display: 'flex', gap: 12, fontSize: 12 }}>
          <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
            <span style={{ width: 10, height: 10, background: 'hsl(var(--monstera))', borderRadius: 2 }} />
            MD
          </span>
          <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
            <span style={{ width: 10, height: 10, background: 'hsl(var(--clay))', borderRadius: 2 }} />
            HK
          </span>
        </div>
      </div>
      <svg
        viewBox={`0 0 ${width} ${height}`}
        width="100%"
        height={height}
        preserveAspectRatio="none"
        role="img"
        aria-label="Daily inbound volume bar chart"
      >
        {/* axis baseline */}
        <line
          x1={pad.left}
          x2={width - pad.right}
          y1={height - pad.bottom}
          y2={height - pad.bottom}
          stroke="hsl(var(--ink) / 0.15)"
        />
        {/* bars */}
        {data.map((d, i) => {
          const total = d.md + d.hk;
          const totalH = (total / max) * innerH;
          const mdH = (d.md / max) * innerH;
          const hkH = (d.hk / max) * innerH;
          const x = pad.left + i * barW + 1;
          const w = Math.max(1, barW - 2);
          const y0 = height - pad.bottom;
          const title = `${d.date}: MD ${d.md}, HK ${d.hk}`;
          return (
            <g key={d.date}>
              <title>{title}</title>
              <rect
                x={x}
                y={y0 - mdH}
                width={w}
                height={mdH}
                fill="hsl(var(--monstera))"
                opacity={0.9}
              />
              <rect
                x={x}
                y={y0 - totalH}
                width={w}
                height={hkH}
                fill="hsl(var(--clay))"
                opacity={0.9}
              />
            </g>
          );
        })}
        {/* labels: first + last + middle date */}
        {[0, Math.floor(data.length / 2), data.length - 1].map((i) => (
          <text
            key={i}
            x={pad.left + i * barW + barW / 2}
            y={height - pad.bottom + 16}
            fontSize="11"
            fill="hsl(var(--ink) / 0.55)"
            textAnchor="middle"
          >
            {data[i]?.date.slice(5) ?? ''}
          </text>
        ))}
        {/* y-axis max label */}
        <text
          x={pad.left - 6}
          y={pad.top + 4}
          fontSize="10"
          fill="hsl(var(--ink) / 0.55)"
          textAnchor="end"
        >
          {max}
        </text>
        <text
          x={pad.left - 6}
          y={height - pad.bottom + 2}
          fontSize="10"
          fill="hsl(var(--ink) / 0.55)"
          textAnchor="end"
        >
          0
        </text>
      </svg>
    </div>
  );
}

function PeakHoursChart({ data }: { data: HourBucket[] }) {
  const width = 720;
  const height = 140;
  const pad = { top: 16, right: 12, bottom: 28, left: 28 };
  const innerW = width - pad.left - pad.right;
  const innerH = height - pad.top - pad.bottom;
  const max = Math.max(1, ...data.map((d) => d.count));
  const barW = innerW / 24;
  return (
    <div
      style={{
        background: 'white',
        border: '1px solid hsl(var(--ink) / 0.1)',
        borderRadius: 12,
        padding: 20,
      }}
    >
      <div
        style={{
          fontSize: 11,
          letterSpacing: '0.1em',
          textTransform: 'uppercase',
          color: 'hsl(var(--ink) / 0.55)',
          marginBottom: 12,
        }}
      >
        Peak hours · inbound messages by hour of day
      </div>
      <svg
        viewBox={`0 0 ${width} ${height}`}
        width="100%"
        height={height}
        preserveAspectRatio="none"
        role="img"
        aria-label="Inbound messages by hour of day"
      >
        <line
          x1={pad.left}
          x2={width - pad.right}
          y1={height - pad.bottom}
          y2={height - pad.bottom}
          stroke="hsl(var(--ink) / 0.15)"
        />
        {data.map((d) => {
          const h = (d.count / max) * innerH;
          const x = pad.left + d.hour * barW + 1;
          const w = Math.max(1, barW - 2);
          const y0 = height - pad.bottom;
          return (
            <g key={d.hour}>
              <title>{`${d.hour}:00 — ${d.count} messages`}</title>
              <rect
                x={x}
                y={y0 - h}
                width={w}
                height={h}
                fill="hsl(var(--monstera))"
                opacity={0.85}
              />
            </g>
          );
        })}
        {[0, 6, 12, 18, 23].map((h) => (
          <text
            key={h}
            x={pad.left + h * barW + barW / 2}
            y={height - pad.bottom + 16}
            fontSize="11"
            fill="hsl(var(--ink) / 0.55)"
            textAnchor="middle"
          >
            {h}:00
          </text>
        ))}
      </svg>
    </div>
  );
}

export function AnalyticsPage() {
  const navigate = useNavigate();
  const { user, signOut } = useAuth();
  const initials = (user?.email ?? 'K').charAt(0).toUpperCase();
  const { data, isLoading, error } = useAnalytics();

  return (
    <>
      <header className="topnav">
        <div className="wrap">
          <div className="topnav-inner">
            <a href="/" className="logo-mark" aria-label="Market Dwellings">
              <Logo />
            </a>
            <nav
              className="viewnav"
              aria-label="Analytics nav"
              style={{ display: 'flex', alignItems: 'center', gap: 10 }}
            >
              <button type="button" onClick={() => navigate('/')}>
                <span aria-hidden style={{ marginRight: 6 }}>←</span>
                Back to Standup
              </button>
            </nav>
            <div className="top-right">
              <button
                type="button"
                className="avatar"
                aria-label="Account menu — sign out"
                title={user?.email ?? 'Signed in'}
                onClick={() => void signOut()}
              >
                {initials}
              </button>
            </div>
          </div>
        </div>
      </header>

      <div className="wrap" style={{ paddingTop: 24, paddingBottom: 48 }}>
        <h1 className="display h2" style={{ marginTop: 0, marginBottom: 6 }}>
          Analytics
        </h1>
        <div
          style={{
            fontSize: 13,
            color: 'hsl(var(--ink) / 0.55)',
            marginBottom: 24,
          }}
        >
          Last 30 days · refreshes every 5 minutes
        </div>

        {isLoading ? (
          <div style={{ color: 'hsl(var(--ink) / 0.55)' }}>Loading analytics…</div>
        ) : error ? (
          <div style={{ color: 'hsl(var(--clay))' }}>
            Couldn't load analytics: {(error as Error).message}
          </div>
        ) : !data ? null : (
          <>
            {/* Row of 3 KPI cards */}
            <div
              style={{
                display: 'grid',
                gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))',
                gap: 16,
                marginBottom: 20,
              }}
            >
              <KpiCard
                label="Median first reply"
                value={formatHours(data.medianFirstReplyHours)}
                sub={`${data.replySampleSize} replied threads`}
              />
              <KpiCard
                label="Haiku acceptance"
                value={formatPct(data.haikuAcceptancePct)}
                sub={`${data.haikuSampleSize} drafted threads`}
              />
              <KpiCard
                label="Backlog age (median)"
                value={formatHours(data.backlogAgeHours)}
                sub={`${data.backlogCount} open threads`}
              />
            </div>

            <div style={{ marginBottom: 20 }}>
              <DailyVolumeChart data={data.dailyVolume} />
            </div>

            <div>
              <PeakHoursChart data={data.peakHours} />
            </div>
          </>
        )}
      </div>
    </>
  );
}
